import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { CommonService } from 'src/app/core/services/common.service';
import { ReportRecordService } from 'src/app/core/services/report-record.service';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadFormID, SyncData } from 'src/app/core/state/_actions/form.actions';
import { LoadColumns, LoadDatatable } from 'src/app/core/state/_actions/report.actions';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { DetailsRelation, RequestDatatable } from 'src/app/core/state/_models/report.models';
import { getFormOperations } from 'src/app/core/state/_selectors/form.selector';
import { getDatatable, getReportColumns } from 'src/app/core/state/_selectors/report.selector';
import { alertPresets, AlertService } from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';
import { ACTION_WIDTH_THRESHOLD_PX } from 'src/app/shared/constants';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';

@Component({
  selector: 'app-record-common-templates',
  templateUrl: './record-common-templates.component.html',
  styleUrls: ['./record-common-templates.component.scss']
})
export class RecordCommonTemplatesComponent implements OnInit, OnChanges {
  @ViewChild('tabDisplayMssage') tabDisplayMssageDialog!: TemplateRef<any>;
  @ViewChild('displayMessage') displayMessageDialog!: TemplateRef<any>;
  @ViewChild('chatPdf') chatPdf!: TemplateRef<any>;
  @ViewChild('embeddedReport') embeddedReport!: TemplateRef<any>;
  @ViewChild('tabReportSection') tabReportSection!: TemplateRef<any>;

  @Input() summaryfieldId: string = '';
  @Input() recordId: string = '';
  @Input() reports: { [key: string]: any } = {};
  @Input() relationTabs: any[] = [];
  @Input() reportDetails: any = null;
  @Input() isInViewMode: boolean = false;
  @Input() currentTabIndex: number = 0;
  @Output() messageDialogClosed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() refreshReportDetails: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() matchRecordOutput: EventEmitter<any> = new EventEmitter<any>();
  @Output() openRelatedModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() openAddRelatedModalOutput: EventEmitter<any> = new EventEmitter<any>();

  messages: string[] = [];
  tabSpecificMessages: { [key: number]: string[] } = {};
  currentRelationTab: number = 0;
  datatable: any = [];
  columns: any = [];
  dataset: any = [];
  subscribers = new SubSink();
  calculatedActionWidths: { [reportKey: string]: number } = {};
  shouldCollapseActionsMap: { [reportKey: string]: boolean } = {};
  showDatatables: boolean = true;
  
  // Feature flag for accessibility
  enableDataTableAccessibility$!: Observable<LDFlagValue>;
  enableDataTableAccessibilityValue: boolean = false;

  constructor(
    private readonly route: RouteService,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly action: ActionService,
    public reportRecordService: ReportRecordService,
    public commonService: CommonService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ldService: LaunchDarklyService
  ) { }

  ngOnInit(): void {
    // Initialize feature flag observable
    this.enableDataTableAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_DATA_TABLE_ACCESSIBILITY,
      false
    );
    
    // Subscribe to get synchronous value for logic
    this.subscribers.add(
      this.enableDataTableAccessibility$.subscribe((value) => {
        this.enableDataTableAccessibilityValue = !!value;
      })
    );
    
    this.loadSubscribers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasReportDetailsChanged = changes['reportDetails'] && !changes['reportDetails'].firstChange;

    if (hasReportDetailsChanged) {
      this.refreshEmbeddedDatatables();
    }
  }

  public refreshEmbeddedDatatables(): void {
    this.showDatatables = false;
    setTimeout(() => {
      this.showDatatables = true;
      this.cdr.markForCheck();
    }, 0);
  }

  setActionColumnWidth(reportKey: string, report: any, isMatchReport: boolean = false) {
    // Bifurcate base width based on feature flag
    // FLAG OFF: Original value of 25
    // FLAG ON: New value of 0
    let width = this.enableDataTableAccessibilityValue ? 0 : 25; // Base padding/margin

    // View button
    if (this.isViewIconVisible(report.TableKey)) {
      width += 60;
    }

    if (!this.isProcessReport(reportKey) &&
      (this.reportRecordService.isAdminOrSuperAdmin() ||
        this.reportRecordService.isAuthorizedParticipant('editRecord', report.TableKey))) {
      width += 55;
    }

    // Match button - check if currently visible (flag tracks current state)
    if (this.isMatchIconVisible(reportKey, report.TableKey)) {
      width += 65;
    }

    // Process button - check if currently visible (flag tracks current state)
    if (this.isProcessReport(reportKey) && this.reportRecordService.isAdminOrSuperAdmin()) {
      width += 75;
    }

    // Add extra space to the right
    width += 25;

    // Store calculated width
    this.calculatedActionWidths[`${reportKey}:${isMatchReport ? 'MATCH' : ''}`] = width;

    // Determine if THIS table should collapse
    this.shouldCollapseActionsMap[`${reportKey}:${isMatchReport ? 'MATCH' : ''}`] = width > ACTION_WIDTH_THRESHOLD_PX;
  }

  getActionColumnWidth(reportKey: string, isMatchReport: boolean): number {
    // Initialize width if not calculated yet
    if (this.calculatedActionWidths[`${reportKey}:${isMatchReport ? 'MATCH' : ''}`] === undefined) {
      const report = this.getReportByKey(reportKey);
      if (report) {
        this.setActionColumnWidth(reportKey, report, isMatchReport);
      }
    }
    return this.calculatedActionWidths[`${reportKey}:${isMatchReport ? 'MATCH' : ''}`] || 70; // Default fallback
  }

  isActionsCollapsed(reportKey: string, isMatchReport: boolean = false): boolean {
    // Ensure width is calculated
    this.getActionColumnWidth(reportKey, isMatchReport);
    return this.shouldCollapseActionsMap[`${reportKey}:${isMatchReport ? 'MATCH' : ''}`] || this.commonService.is_small_screen;
  }

  getReportByKey(reportKey: string): any {
    return this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.ReportKey === reportKey ||
        relation.EmbeddedSetting?.MatchRecordsReportKey === reportKey
    );
  }

  loadSubscribers() {
    this.subscribers.add(
      combineLatest([
        this.store.pipe(
          select(getReportColumns),
          filter((columns) => !!columns)
        ),
        this.store.pipe(
          select(getDatatable),
          filter((data) => !!data)
        ),
      ]).subscribe((data) => {
        this.datatable.forEach((value: any) => {
          if (
            data[0][value.tableId] != undefined &&
            data[1][value.tableId] != undefined
          ) {
            let column = data[0][value.tableId][value.reportId];
            let dataset = data[1][value.tableId][value.reportId];
            this.columns[value.reportId] = column;
            this.dataset[value.reportId] = dataset;
          }
        });
      }));
  }

  chatClosed(col: any) {
    const chatbox: any = document.getElementById(
      `divContainer-${col.FieldKey}-${col.IsHeader}`
    );
    if (chatbox) {
      chatbox.style.display = chatbox?.style.display == 'none' ? 'block' : 'none';
    }
  }

  isChatBotVisible(col: any) {
    return (
      document.getElementById(`divContainer-${col.FieldKey}-${col.IsHeader}`)
        ?.style.display != 'none'
    );
  }

  closeMessageModal(isTabSpecific: boolean = false) {
    this.messageDialogClosed.emit(isTabSpecific);
  }

  /**
   * Determines if a dropdown menu should be displayed for the report.
   * The dropdown is shown if the user has permissions to add/edit records or edit the report/form.
   * @param report The report object containing metadata about the report.
   * @returns boolean True if the dropdown should be displayed, false otherwise.
   */
  hasDropdown(report: any): boolean {
    const tableKey = report.TableKey;
    const isDatatableOrGrid =
      report.ReportType === 'datatable' ||
      (report.ReportType === 'flex' && report.FlexReportType === 'grid') || !report.ShowRelatedRecords;

    const canAddRecord = this.reportRecordService.isAdminOrSuperAdmin() || this.reportRecordService.isAuthorizedParticipant('addRecord', tableKey);
    const canEditReport = this.reportRecordService.isAdminOrSuperAdmin() || this.reportRecordService.isAuthorizedParticipant('editReport', tableKey);
    const canEditForm = this.reportRecordService.isAdminOrSuperAdmin() || this.reportRecordService.isAuthorizedParticipant('editForm', tableKey);

    return isDatatableOrGrid && (canAddRecord || canEditReport || canEditForm);
  }

  /**
   * Generates a navigation link for the report.
   * @param report The report object containing metadata like ReportKey and TableKey.
   * @returns string The URL for navigating to the report's page.
   */
  getReportLink(report: any): string {
    const flexReportType = report.FlexReportType === 'chart' ? 'chart' : 'summary';
    const reportType = report.ReportType === 'flex' ? flexReportType : report.ReportType;
    return `/apps/${report.ApplicationKey}/tables/${report.TableKey}/reports/${reportType}/view/${report.ReportKey}`;
  }

  setCurrentRelationTab(Tab: number, tabIndex: number) {
    this.currentRelationTab = Tab;
  }

  onToggleChange(event: any, report: any): void {
    report.ShowRelatedRecords = !report.ShowRelatedRecords;
    this.setActionColumnWidth(report.ReportKey, report);
  }

  getTableName(detailsRelation: DetailsRelation) {
    if (detailsRelation.TableSingularName)
      return detailsRelation.TableSingularName;
    return detailsRelation.TableName;
  }

  payloadListener = (
    tableId: string,
    reportId: any,
    referenceFieldKey: string,
    lookupFieldValue: any,
    event: RequestDatatable
  ) => {
    if (event.FromEmbeddedReport) return;
    this.datatable.push({
      reportId: reportId,
      tableId: tableId,
    });

    this.store.dispatch(new LoadFormID(tableId, reportId));
    this.store.dispatch(new LoadColumns(tableId, reportId));
    this.store.dispatch(
      new LoadDatatable(tableId, reportId, {
        ...event,
        TableKey: tableId,
        ReportKey: reportId,
        ReferenceFieldKey: referenceFieldKey,
        LookupFieldValue: lookupFieldValue,
        IsEmbeddedReport: true,
        SummaryFieldKey: this.summaryfieldId,
      })
    );
  };

  onTableUpdate(event: any) {
    if (event) {
      this.refreshReportDetails.emit(true);
    }
  }

  isReportVisible(reportDetails: any) {
    return !reportDetails!.DetailsRelation.every(
      (relation: any) => relation?.isReportInForm
    );
  }

  getProcessState = (id: number, tableId: any, reportId: any) => {
    return (
      '/apps/' +
      `${this.route.getApplicationKey()}` +
      '/tables/' +
      `${tableId}` +
      '/reports' +
      '/datatable' +
      '/view/' +
      reportId +
      '/view-process/' +
      id
    );
  };

  redirectToEditReportForm(report: any, action: string) {
    const isMatchReport = this.isMatchRecordAllowed(report.ReportKey) ?? this.isMatchRelatedRecordAllowed(report.ReportKey)
    if (action === 'editForm') {
      this.route.navigate([
        '/apps',
        `${this.route.getApplicationKey()}`,
        'tables',
        `${report.TableKey}`,
        'forms',
        'web',
        `${!isMatchReport ? report.FormKey : report.EmbeddedSetting.MatchRecordsRelatedFormKey}`,
      ]);
    } else if (action === 'editReport') {
      this.route.navigate([
        '/apps',
        `${this.route.getApplicationKey()}`,
        'tables',
        `${report.TableKey}`,
        'reports',
        `${(report.ReportType === 'datatable' || isMatchReport) ? 'datatable' : 'summary'}`,
        `${!isMatchReport ? report.ReportKey : report.EmbeddedSetting.MatchRecordsReportKey}`,
      ]);
    }
  }

  isMatchRecordAllowed(reportKey: string) {
    const report = this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.EmbeddedSetting?.MatchRecordsReportKey === reportKey
    )
    if (report && Object.keys(this.reports).includes(report.ReportKey)) {
      return !this.reports[report.ReportKey].ShowRelatedRecords
    }
    return report && !this.relationTabs.find((tab: any) => tab.TableKey === report?.TableKey)?.ShowRelatedRecords
  }

  isMatchRelatedRecordAllowed(reportKey: string) {
    const report = this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.ReportKey === reportKey
    )
    if (report && Object.keys(this.reports).includes(report.ReportKey)) {
      return !this.reports[report.ReportKey].ShowRelatedRecords
    }
    return report && !this.relationTabs.find((tab: any) => tab.TableKey === report?.TableKey)?.ShowRelatedRecords
  }

  getEmbeddedReport(reportKey: string) {
    const report = this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.EmbeddedSetting && relation.EmbeddedSetting.MatchRecordsReportKey === reportKey
    )
    if (report && Object.keys(this.reports).includes(report.ReportKey)) {
      return this.reports[report.ReportKey]
    }
    return this.relationTabs?.find((tab: any) => tab.TableKey === report?.TableKey)
  }

  getEmbeddedRelation(tableKey: string) {
    return this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.TableKey === tableKey
    )
  }

  openAddEditRelatedModal(report: any, id: string, tableId: string, reportId: string) {
    this.openAddRelatedModalOutput.emit({ report, id, tableId, reportId });
  }

  matchRecord = async (tableId: string, id: string, reportId: string) => {
    const payload: any = {
      ApplicationTableKey: tableId,
      ActionType: 'update',
      Where: { Rid: id },
    };

    await this.alert.fire(<SweetAlertOptions>{
      ...alertPresets.delete,
      title: 'Are you sure?',
      text: 'This action will attach the record with parent table',
      confirmButtonText: 'Yes, match record.',
    })
      .then(async (res) => {
        if (res.isConfirmed) {
          const obj = {
            Name: this.getEmbeddedRelation(tableId)?.ReferenceFieldFieldName,
            Info: null,
            Value: this.recordId,
            Type: 'INT',
            Base64EncodedFile: '',
          }
          payload.FieldsList = [obj];
          try {
            await this.action.dispatch(
              OpTypes.UPDATE,
              new SyncData(tableId, [payload]),
              getFormOperations
            );
            this.getEmbeddedReport(reportId).ShowRelatedRecords = true;
            this.refreshReportDetails.emit(true);
          } catch (err) {
            console.error('Bulk Match failed with error: ', err);
          }
        }
      })
  }

  isProcessReport(reportKey: string) {
    return !!this.reportDetails?.DetailsRelation?.find(
      (relation: any) => relation.ReportKey === reportKey
    )?.ProcedureKey;
  }

  getEditLink(id: any, tableId: any, reportId: any) {
    let detailRelation: any = this.reportDetails.DetailsRelation.find(
      (tab: any) => tab.ReportKey === reportId
    );

    if (!detailRelation && this.isMatchRecordAllowed(reportId)) {
      detailRelation = this.reportDetails.DetailsRelation.find(
        (tab: any) => tab.EmbeddedSetting && tab.EmbeddedSetting.MatchRecordsReportKey === reportId
      );
    }
    const formKey = !(this.isMatchRecordAllowed(reportId) || this.isMatchRelatedRecordAllowed(reportId)) ? detailRelation.FormKey : detailRelation.EmbeddedSetting?.MatchRecordsRelatedFormKey;
    return (
      `${location.origin}` +
      '/apps/' +
      `${this.route.getApplicationKey()}` +
      '/tables/' +
      `${tableId}` +
      '/reports' +
      '/datatable' +
      '/view/' +
      reportId +
      '/form/' +
      `${formKey}` +
      '/edit/' +
      id
    );
  }

  editRow(event: any, id: any, tableId: any, reportId: any) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    let detailRelation: any = this.reportDetails.DetailsRelation.find(
      (tab: any) => tab.ReportKey === reportId
    );

    if (!detailRelation && this.isMatchRecordAllowed(reportId)) {
      detailRelation = this.reportDetails.DetailsRelation.find(
        (tab: any) => tab.EmbeddedSetting && tab.EmbeddedSetting.MatchRecordsReportKey === reportId
      );
    }
    this.openAddEditRelatedModal(detailRelation, id, tableId, reportId);
  }

  getViewLink(row: any, tableId: any, reportId: any, forRouteLink: boolean = true) {
    const appKey = this.route.getApplicationKey();
    const base = `/apps/${appKey}/tables/${tableId}/reports`;
    const isProcess = this.isProcessReport(reportId);

    if (isProcess) {
      const { process_actor_ui_type, process_is_external_url, process_url } = row;

      if (process_actor_ui_type === 1) {
        if (process_is_external_url) return process_url;

        const val = process_url.split('/app');
        const appVal = val[1]?.split('/') ?? [];
        appVal.splice(0, 1, '/apps');
        return location.origin + appVal.join('/');
      }

      // Internal process view links
      const viewType = 'process';
      return `${base}/${viewType}/view/${row.id}/${reportId}`;
    }

    // --- Normal (non-process) reports ---
    const parts = [
      '/apps',
      appKey,
      'tables',
      tableId,
      'reports',
      'details',
      'view',
      row.id,
      reportId,
    ];

    return forRouteLink ? parts : location.origin + parts.join('/');
  }

  viewRow = (event: any, row: any, tableId: any, reportId: any) => {
    if (event.ctrlKey || event.metaKey) return; // skip for new tab
    event.preventDefault();
    event.stopPropagation();

    const isProcess = this.isProcessReport(reportId);
    let detailRelation = this.reportDetails.DetailsRelation.find(
      (tab: any) => tab.ReportKey === reportId
    );

    if (!detailRelation && this.isMatchRecordAllowed(reportId)) {
      detailRelation = this.reportDetails.DetailsRelation.find(
        (tab: any) =>
          tab.EmbeddedSetting?.MatchRecordsReportKey === reportId
      );
    }

    // --- Process report handling ---
    if (isProcess) {
      const { process_actor_ui_type, process_is_external_url, process_url, process_form_key } = row;

      if (process_actor_ui_type === 1) {
        // Type 1: external/internal link
        if (process_is_external_url) {
          return window.open(process_url, '_blank');
        }

        const val = process_url.split('/app');
        const appVal = val[1]?.split('/') ?? [];
        appVal.splice(0, 1, '/apps');
        return this.route.navigate(appVal);
      }

      // Type 2 or default internal process navigation
      if (process_actor_ui_type === 2) {
        return this.openViewRelatedRecord(detailRelation, row.id, tableId, reportId, process_form_key);
      }

      return this.route.navigate([
        '/apps',
        this.route.getApplicationKey(),
        'tables',
        tableId,
        'reports',
        'process',
        'view',
        row.id,
        reportId,
      ]);
    }

    // --- Non-process reports ---
    this.openViewRelatedRecord(detailRelation, row.id, tableId, reportId);
  };

  openViewRelatedRecord(report: any, id: string, tableId: string, reportId: string, formKey?: string) {
    this.openRelatedModal.emit({ report, id, tableId, reportId, formKey });
  }

  isViewIconVisible(tableId: string): boolean {
    return this.reportRecordService.isAdminOrSuperAdmin() || this.reportRecordService.isAuthorizedParticipant('viewRecord', tableId);
  }

  isEditIconVisible(isEdit: boolean | undefined, tableId: string, reportId: string): boolean {
    return (isEdit === undefined || isEdit) &&
      (this.reportRecordService.isAdminOrSuperAdmin() ||
        this.reportRecordService.isAuthorizedParticipant('editRecord', tableId)) &&
      !this.isProcessReport(reportId)
  }

  isMatchIconVisible(reportId: string, tableId: any): boolean {
    const isInMatchMode = this.isMatchRecordAllowed(reportId);

    return isInMatchMode &&
      (this.reportRecordService.isAdminOrSuperAdmin() ||
        this.reportRecordService.isAuthorizedParticipant('editRecord', tableId));
  }

  isProcessIconVisible(procedureKey: string, process_is_initiated: boolean, reportId: string): boolean {
    return !!(this.isProcessReport(reportId) && this.reportRecordService.isAdminOrSuperAdmin() && procedureKey && process_is_initiated);
  }
}
